import { useState, useEffect } from 'react';
import api, { downloadFile } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { FileSpreadsheet, FileText, Download, Building2, Users, Receipt, Clock } from 'lucide-react';

function ExportButtons({ reportType, exporting, onExport }) {
  return (
    <div className="flex gap-2">
      <Button
        variant="outline"
        size="sm"
        className="rounded-sm gap-2"
        onClick={() => onExport(reportType, 'excel')}
        disabled={!!exporting}
        data-testid={`export-${reportType}-excel`}
      >
        <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
        {exporting === `${reportType}-excel` ? 'Exporting...' : 'Excel'}
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="rounded-sm gap-2"
        onClick={() => onExport(reportType, 'pdf')}
        disabled={!!exporting}
        data-testid={`export-${reportType}-pdf`}
      >
        <FileText className="w-4 h-4 text-red-500" />
        {exporting === `${reportType}-pdf` ? 'Exporting...' : 'PDF'}
      </Button>
    </div>
  );
}

export default function ReportsPage() {
  const [fy, setFy] = useState('');
  const [clientRevenue, setClientRevenue] = useState(null);
  const [recruiterRevenue, setRecruiterRevenue] = useState(null);
  const [gstSummary, setGstSummary] = useState(null);
  const [outstanding, setOutstanding] = useState(null);
  const [loading, setLoading] = useState({});
  const [exporting, setExporting] = useState('');

  const fyOptions = () => {
    const now = new Date();
    const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return Array.from({ length: 3 }, (_, i) => {
      const y = year - i;
      return `${y}-${String(y + 1).slice(-2)}`;
    });
  };

  const currentFy = () => {
    const now = new Date();
    const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${year}-${String(year + 1).slice(-2)}`;
  };

  useEffect(() => { setFy(currentFy()); }, []);

  const fetchReport = async (type) => {
    setLoading(prev => ({ ...prev, [type]: true }));
    try {
      const fyParam = fy ? `?financial_year=${fy}` : '';
      if (type === 'client') {
        const res = await api.get(`/finance/reports/client-revenue${fyParam}`);
        setClientRevenue(res.data);
      } else if (type === 'recruiter') {
        const res = await api.get(`/finance/reports/recruiter-revenue${fyParam}`);
        setRecruiterRevenue(res.data);
      } else if (type === 'gst') {
        const res = await api.get(`/finance/reports/gst-summary${fyParam}`);
        setGstSummary(res.data);
      } else if (type === 'outstanding') {
        const res = await api.get('/finance/payments/outstanding');
        setOutstanding(res.data);
      }
    } catch {
      toast.error(`Failed to load ${type} report`);
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleExport = async (reportType, format) => {
    const key = `${reportType}-${format}`;
    setExporting(key);
    try {
      const fyParam = fy ? `&financial_year=${fy}` : '';
      let url, filename;

      if (reportType === 'client') {
        url = `/finance/reports/client-revenue/export?format=${format}${fyParam}`;
        filename = `client_revenue_${fy}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      } else if (reportType === 'recruiter') {
        url = `/finance/reports/recruiter-revenue/export?format=${format}${fyParam}`;
        filename = `recruiter_revenue_${fy}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      } else if (reportType === 'gst') {
        url = `/finance/reports/gst-summary/export?format=${format}${fyParam}`;
        filename = `gst_summary_${fy}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      } else if (reportType === 'outstanding') {
        url = `/finance/reports/outstanding-aging/export?format=${format}`;
        filename = `outstanding_aging.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      }

      await downloadFile(url, filename);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting('');
    }
  };

  const fmt = (val) => `₹${(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div data-testid="reports-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">Financial Reports</h2>
          <p className="text-sm text-muted-foreground font-body mt-1">Generate and export financial reports</p>
        </div>
        <Select value={fy} onValueChange={setFy}>
          <SelectTrigger className="w-40 rounded-sm" data-testid="fy-selector">
            <SelectValue placeholder="Financial Year" />
          </SelectTrigger>
          <SelectContent>
            {fyOptions().map(f => <SelectItem key={f} value={f}>FY {f}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="client" className="w-full" onValueChange={(tab) => {
        if (tab === 'client' && !clientRevenue) fetchReport('client');
        if (tab === 'recruiter' && !recruiterRevenue) fetchReport('recruiter');
        if (tab === 'gst' && !gstSummary) fetchReport('gst');
        if (tab === 'outstanding' && !outstanding) fetchReport('outstanding');
      }}>
        <TabsList className="mb-4">
          <TabsTrigger value="client" data-testid="tab-client-revenue" className="gap-2">
            <Building2 className="w-3.5 h-3.5" /> Client Revenue
          </TabsTrigger>
          <TabsTrigger value="recruiter" data-testid="tab-recruiter-revenue" className="gap-2">
            <Users className="w-3.5 h-3.5" /> Recruiter Revenue
          </TabsTrigger>
          <TabsTrigger value="gst" data-testid="tab-gst-summary" className="gap-2">
            <Receipt className="w-3.5 h-3.5" /> GST Summary
          </TabsTrigger>
          <TabsTrigger value="outstanding" data-testid="tab-outstanding" className="gap-2">
            <Clock className="w-3.5 h-3.5" /> Outstanding Aging
          </TabsTrigger>
        </TabsList>

        {/* Client Revenue */}
        <TabsContent value="client">
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => fetchReport('client')} disabled={loading.client}>
              {loading.client ? 'Loading...' : 'Refresh'}
            </Button>
            <ExportButtons reportType="client" exporting={exporting} onExport={handleExport} />
          </div>
          {clientRevenue ? (
            <>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <Card className="p-4 border-border"><p className="text-xs text-muted-foreground uppercase tracking-widest">Base Amount</p><p className="text-lg font-heading font-bold text-foreground">{fmt(clientRevenue.totals?.base)}</p></Card>
                <Card className="p-4 border-border"><p className="text-xs text-muted-foreground uppercase tracking-widest">GST</p><p className="text-lg font-heading font-bold text-foreground">{fmt(clientRevenue.totals?.gst)}</p></Card>
                <Card className="p-4 border-border"><p className="text-xs text-muted-foreground uppercase tracking-widest">Total Revenue</p><p className="text-lg font-heading font-bold text-emerald-600">{fmt(clientRevenue.totals?.total)}</p></Card>
              </div>
              <div className="border border-border bg-card rounded-sm overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="text-xs uppercase tracking-widest">Client</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">State</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Invoices</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Base Amount</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">GST</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Total</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {clientRevenue.report?.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No revenue data for FY {fy}</TableCell></TableRow>
                    ) : clientRevenue.report?.map((r) => (
                      <TableRow key={`${r.client_name}-${r.state}`}>
                        <TableCell className="font-medium">{r.client_name}</TableCell>
                        <TableCell>{r.state}</TableCell>
                        <TableCell className="text-right">{r.invoice_count}</TableCell>
                        <TableCell className="text-right font-mono">{fmt(r.base_amount)}</TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">{fmt(r.gst_amount)}</TableCell>
                        <TableCell className="text-right font-mono font-medium">{fmt(r.total_amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <Card className="p-8 text-center border-border">
              <Building2 className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground text-sm">Click Refresh to load client revenue report</p>
            </Card>
          )}
        </TabsContent>

        {/* Recruiter Revenue */}
        <TabsContent value="recruiter">
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => fetchReport('recruiter')} disabled={loading.recruiter}>
              {loading.recruiter ? 'Loading...' : 'Refresh'}
            </Button>
            <ExportButtons reportType="recruiter" exporting={exporting} onExport={handleExport} />
          </div>
          {recruiterRevenue ? (
            <>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <Card className="p-4 border-border"><p className="text-xs text-muted-foreground uppercase tracking-widest">Total Revenue</p><p className="text-lg font-heading font-bold text-emerald-600">{fmt(recruiterRevenue.totals?.revenue)}</p></Card>
                <Card className="p-4 border-border"><p className="text-xs text-muted-foreground uppercase tracking-widest">Total Placements</p><p className="text-lg font-heading font-bold text-foreground">{recruiterRevenue.totals?.placements || 0}</p></Card>
              </div>
              <div className="border border-border bg-card rounded-sm overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="text-xs uppercase tracking-widest">Recruiter</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Employee ID</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Revenue</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Placements</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {recruiterRevenue.report?.length === 0 ? (
                      <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No recruiter data for FY {fy}</TableCell></TableRow>
                    ) : recruiterRevenue.report?.map((r) => (
                      <TableRow key={r.employee_id || r.recruiter_name}>
                        <TableCell className="font-medium">{r.recruiter_name}</TableCell>
                        <TableCell className="font-mono text-sm">{r.employee_id}</TableCell>
                        <TableCell className="text-right font-mono font-medium">{fmt(r.revenue)}</TableCell>
                        <TableCell className="text-right">{r.placement_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <Card className="p-8 text-center border-border">
              <Users className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground text-sm">Click Refresh to load recruiter revenue report</p>
            </Card>
          )}
        </TabsContent>

        {/* GST Summary */}
        <TabsContent value="gst">
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => fetchReport('gst')} disabled={loading.gst}>
              {loading.gst ? 'Loading...' : 'Refresh'}
            </Button>
            <ExportButtons reportType="gst" exporting={exporting} onExport={handleExport} />
          </div>
          {gstSummary ? (
            <>
              <div className="grid grid-cols-5 gap-3 mb-4">
                <Card className="p-3 border-border"><p className="text-[10px] text-muted-foreground uppercase tracking-widest">Base</p><p className="text-sm font-heading font-bold text-foreground">{fmt(gstSummary.totals?.base)}</p></Card>
                <Card className="p-3 border-border"><p className="text-[10px] text-muted-foreground uppercase tracking-widest">IGST</p><p className="text-sm font-heading font-bold text-foreground">{fmt(gstSummary.totals?.igst)}</p></Card>
                <Card className="p-3 border-border"><p className="text-[10px] text-muted-foreground uppercase tracking-widest">CGST</p><p className="text-sm font-heading font-bold text-foreground">{fmt(gstSummary.totals?.cgst)}</p></Card>
                <Card className="p-3 border-border"><p className="text-[10px] text-muted-foreground uppercase tracking-widest">SGST</p><p className="text-sm font-heading font-bold text-foreground">{fmt(gstSummary.totals?.sgst)}</p></Card>
                <Card className="p-3 border-border"><p className="text-[10px] text-muted-foreground uppercase tracking-widest">Total GST</p><p className="text-sm font-heading font-bold text-amber-600">{fmt(gstSummary.totals?.total_gst)}</p></Card>
              </div>
              <div className="border border-border bg-card rounded-sm overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="text-xs uppercase tracking-widest">Invoice #</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Date</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Client</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Client GST</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Base</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">IGST</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">CGST</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">SGST</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Total GST</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {gstSummary.report?.length === 0 ? (
                      <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No GST data for FY {fy}</TableCell></TableRow>
                    ) : gstSummary.report?.map((r) => (
                      <TableRow key={r.invoice_number}>
                        <TableCell className="font-mono text-sm">{r.invoice_number}</TableCell>
                        <TableCell className="text-sm">{r.invoice_date}</TableCell>
                        <TableCell>{r.client_name}</TableCell>
                        <TableCell className="font-mono text-xs">{r.client_gst || '-'}</TableCell>
                        <TableCell className="text-right font-mono">{fmt(r.base_amount)}</TableCell>
                        <TableCell className="text-right font-mono">{r.igst > 0 ? fmt(r.igst) : '-'}</TableCell>
                        <TableCell className="text-right font-mono">{r.cgst > 0 ? fmt(r.cgst) : '-'}</TableCell>
                        <TableCell className="text-right font-mono">{r.sgst > 0 ? fmt(r.sgst) : '-'}</TableCell>
                        <TableCell className="text-right font-mono font-medium">{fmt(r.total_gst)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <Card className="p-8 text-center border-border">
              <Receipt className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground text-sm">Click Refresh to load GST summary report</p>
            </Card>
          )}
        </TabsContent>

        {/* Outstanding Aging */}
        <TabsContent value="outstanding">
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => fetchReport('outstanding')} disabled={loading.outstanding}>
              {loading.outstanding ? 'Loading...' : 'Refresh'}
            </Button>
            <ExportButtons reportType="outstanding" exporting={exporting} onExport={handleExport} />
          </div>
          {outstanding ? (
            <>
              <div className="grid grid-cols-4 gap-4 mb-4">
                <Card className="p-4 border-border border-l-4 border-l-emerald-500"><p className="text-xs text-muted-foreground uppercase tracking-widest">Current</p><p className="text-lg font-heading font-bold text-emerald-600">{fmt(outstanding.totals?.current)}</p></Card>
                <Card className="p-4 border-border border-l-4 border-l-amber-500"><p className="text-xs text-muted-foreground uppercase tracking-widest">0-30 Days</p><p className="text-lg font-heading font-bold text-amber-600">{fmt(outstanding.totals?.['0-30'])}</p></Card>
                <Card className="p-4 border-border border-l-4 border-l-orange-500"><p className="text-xs text-muted-foreground uppercase tracking-widest">30-60 Days</p><p className="text-lg font-heading font-bold text-orange-600">{fmt(outstanding.totals?.['30-60'])}</p></Card>
                <Card className="p-4 border-border border-l-4 border-l-red-500"><p className="text-xs text-muted-foreground uppercase tracking-widest">60+ Days</p><p className="text-lg font-heading font-bold text-red-600">{fmt(outstanding.totals?.['60+'])}</p></Card>
              </div>
              <Card className="p-4 border-border mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Total Outstanding</span>
                  <span className="text-xl font-heading font-bold text-foreground">{fmt(outstanding.total_outstanding)}</span>
                </div>
              </Card>
              <div className="border border-border bg-card rounded-sm overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="text-xs uppercase tracking-widest">Invoice #</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Client</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Due Date</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Total</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Outstanding</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Aging</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(() => {
                      const allInvoices = [...(outstanding.aging?.current || []), ...(outstanding.aging?.['0-30'] || []), ...(outstanding.aging?.['30-60'] || []), ...(outstanding.aging?.['60+'] || [])];
                      if (allInvoices.length === 0) return <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No outstanding invoices</TableCell></TableRow>;
                      return allInvoices.map((inv) => (
                        <TableRow key={inv.invoice_number}>
                          <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                          <TableCell>{inv.client_name}</TableCell>
                          <TableCell className="text-sm">{inv.due_date}</TableCell>
                          <TableCell className="text-right font-mono">{fmt(inv.total_amount)}</TableCell>
                          <TableCell className="text-right font-mono font-medium text-amber-600">{fmt(inv.amount_outstanding)}</TableCell>
                          <TableCell>
                            <Badge className={
                              inv.aging_bucket === 'current' ? 'bg-emerald-100 text-emerald-700' :
                              inv.aging_bucket === '0-30' ? 'bg-amber-100 text-amber-700' :
                              inv.aging_bucket === '30-60' ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'
                            }>{inv.aging_bucket}</Badge>
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <Card className="p-8 text-center border-border">
              <Clock className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground text-sm">Click Refresh to load outstanding aging report</p>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
